import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const KEY = "eyw_consent";

export function getConsent() {
  try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; }
}

export default function CookieBanner() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });
  const boxRef = useRef(null);

  useEffect(() => { if (!getConsent()) setShow(true); }, []);

  // Reserve space so the fixed banner never hides page content/buttons.
  useEffect(() => {
    if (!show) { document.body.style.paddingBottom = ""; return; }
    const apply = () => { document.body.style.paddingBottom = `${(boxRef.current?.offsetHeight || 0) + 16}px`; };
    apply();
    window.addEventListener("resize", apply);
    return () => { window.removeEventListener("resize", apply); document.body.style.paddingBottom = ""; };
  }, [show, customize]);

  const persist = async (consent) => {
    localStorage.setItem(KEY, JSON.stringify({ ...consent, at: new Date().toISOString() }));
    setShow(false);
    if (user) { try { await api.put("/privacy/consent", { ...consent, email_opt_in: consent.marketing, sms_opt_in: false }); } catch { /* ignore */ } }
    window.dispatchEvent(new CustomEvent("eyw-consent", { detail: consent }));
  };

  if (!show) return null;

  return (
    <div ref={boxRef} className="fixed bottom-0 inset-x-0 z-[200] p-3 sm:p-4" data-testid="cookie-banner">
      <div className="max-w-4xl mx-auto bg-[#1A242B] text-white rounded-2xl shadow-2xl p-6">
        {!customize ? (
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <Cookie size={26} className="text-[#D98E73] shrink-0" />
            <p className="text-sm text-white/85 flex-1">We use cookies to run the site and, with your consent, to understand traffic and personalize your experience. See our <Link to="/privacy" className="underline">Privacy Policy</Link>.</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setCustomize(true)} data-testid="cookie-customize" className="text-sm px-4 py-2 rounded-full border border-white/30 hover:bg-white/10">Customize</button>
              <button onClick={() => persist({ analytics: false, marketing: false })} data-testid="cookie-reject" className="text-sm px-4 py-2 rounded-full border border-white/30 hover:bg-white/10">Reject non-essential</button>
              <button onClick={() => persist({ analytics: true, marketing: true })} data-testid="cookie-accept" className="text-sm px-5 py-2 rounded-full bg-[#D98E73] hover:bg-[#c97d63] font-medium">Accept all</button>
            </div>
          </div>
        ) : (
          <div data-testid="cookie-customize-panel">
            <h3 className="font-serif text-lg mb-3">Cookie preferences</h3>
            <div className="space-y-3 text-sm">
              <label className="flex items-center justify-between opacity-60"><span>Strictly necessary <span className="text-white/50">— always on</span></span><input type="checkbox" checked readOnly /></label>
              <label className="flex items-center justify-between"><span>Analytics — help us improve</span><input type="checkbox" data-testid="cookie-analytics-toggle" checked={prefs.analytics} onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })} /></label>
              <label className="flex items-center justify-between"><span>Marketing — personalized offers</span><input type="checkbox" data-testid="cookie-marketing-toggle" checked={prefs.marketing} onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })} /></label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setCustomize(false)} className="text-sm px-4 py-2 rounded-full border border-white/30 hover:bg-white/10">Back</button>
              <button onClick={() => persist(prefs)} data-testid="cookie-save-prefs" className="text-sm px-5 py-2 rounded-full bg-[#D98E73] hover:bg-[#c97d63] font-medium">Save preferences</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
